/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Dropdown, Icon } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

import ScEnvSshConnRowExpanded from './ScEnvSshConnRowExpanded';

class ScEnvironmentSshConnectionRow extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      const key = _.first(this.keyPairsStore.listActive) || {};
      this.networkInterfaces = undefined;
      this.processingSendKey = false;
      this.selectedKeyId = key.id;
    });
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get keyPairsStore() {
    return this.props.keyPairsStore;
  }

  get keyPairOptions() {
    return _.map(this.keyPairsStore.listActive, item => ({ key: item.id, value: item.id, text: item.name }));
  }

  getConnectionStore() {
    return this.envsStore.getScEnvConnectionStore(this.environment.id);
  }

  get connections() {
    return this.environment.getConnections(item => item.scheme === 'ssh');
  }

  get connection() {
    return _.find(this.connections, ['id', this.connectionId]) || {};
  }

  get connectionId() {
    return this.props.connectionId;
  }

  get selectedKeyName() {
    if (_.isEmpty(this.selectedKeyId)) return '';
    return _.get(this.keyPairsStore.getKeyPair(this.selectedKeyId), 'name');
  }

  handleActivate = async () => {
    const keyId = this.selectedKeyId;
    const store = this.getConnectionStore();
    const connectionId = this.connectionId;
    if (_.isEmpty(keyId)) {
      displayError('Please select an SSH key');
      return;
    }

    this.networkInterfaces = undefined;
    this.processingSendKey = true;
    try {
      const result = await store.sendSshKey(connectionId, keyId);
      runInAction(() => {
        this.networkInterfaces = _.get(result, 'networkInterfaces');
      });
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => {
        this.processingSendKey = false;
      });
    }
  };

  handleKeyChange = (e, data) => {
    const value = _.get(data, 'value');
    const changed = value !== this.selectedKeyId;
    this.selectedKeyId = value;
    if (changed) this.networkInterfaces = undefined;
  };

  render() {
    const store = this.keyPairsStore;
    const emptyKeys = _.isEmpty(store.listActive);
    const item = this.connection;
    const networkInterfaces = this.networkInterfaces;
    const options = this.keyPairOptions;
    const selectedKeyId = this.selectedKeyId;
    const selectedKeyName = this.selectedKeyName;

    return (
      <div>
        <div className="connection-row">
          <div className="connection-row-name">{item.name || item.instanceId || 'SSH Connection'}</div>
          <div className="connection-row-actions">
            <div className="ssh-key-selector">
              {emptyKeys ? (
                <span style={{ color: '#6b7280', fontSize: '0.85em' }}>No SSH keys available</span>
              ) : (
                <>
                  <span style={{ color: '#374151', fontSize: '0.9em' }}>Choose SSH key:</span>
                  <Dropdown
                    placeholder="Select SSH Key"
                    selection
                    options={options}
                    value={selectedKeyId}
                    onChange={this.handleKeyChange}
                    disabled={emptyKeys || this.processingSendKey}
                    className="ssh-key-dropdown"
                  />
                </>
              )}
              <button
                className="connection-btn connection-btn-primary"
                onClick={this.handleActivate}
                disabled={emptyKeys || this.processingSendKey}
                data-testid="use-ssh-key-button"
              >
                {this.processingSendKey ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="key" style={{ margin: 0 }} />}
                Use this SSH Key
              </button>
            </div>
          </div>
        </div>

        {networkInterfaces && (
          <ScEnvSshConnRowExpanded
            networkInterfaces={networkInterfaces}
            keyName={selectedKeyName}
            keyId={selectedKeyId}
            connectionId={item.id}
            scEnvironmentsStore={this.envsStore}
            scEnvironment={this.environment}
          />
        )}
      </div>
    );
  }
}

decorate(ScEnvironmentSshConnectionRow, {
  envsStore: computed,
  environment: computed,
  keyPairsStore: computed,
  connections: computed,
  connection: computed,
  connectionId: computed,
  keyPairOptions: computed,
  selectedKeyName: computed,
  selectedKeyId: observable,
  networkInterfaces: observable,
  processingSendKey: observable,
  handleActivate: action,
  handleKeyChange: action,
});

export default inject('scEnvironmentsStore', 'keyPairsStore')(withRouter(observer(ScEnvironmentSshConnectionRow)));
